/**
 * データ保存前にundefined値を除去するユーティリティ関数
 * Firestoreに保存できないundefined値を安全に除去
 */
export const cleanUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  // Dateオブジェクトは再帰処理せずそのまま返す
  if (obj instanceof Date) {
    return obj;
  }
  
  if (typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(cleanUndefinedValues).filter(item => item !== undefined);
  }
  
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = cleanUndefinedValues(value);
    }
  }
  
  return cleaned;
};

/**
 * EXIF日付データを安全にDateオブジェクトに変換
 * @param exifDate - EXIF日付データ（文字列、数値、またはDateオブジェクト）
 * @returns 正しく変換されたDateオブジェクト、または無効な場合はnull
 */
export const parseExifDate = (exifDate: any): Date | null => {
  if (!exifDate) return null;
  
  // 既にDateオブジェクトの場合
  if (exifDate instanceof Date) {
    return isNaN(exifDate.getTime()) ? null : exifDate;
  }
  
  // 数値の場合（Unixタイムスタンプなど）
  if (typeof exifDate === 'number') {
    try {
      // タイムスタンプが秒単位の場合（10桁）はミリ秒に変換
      const timestamp = exifDate < 10000000000 ? exifDate * 1000 : exifDate;
      const parsedDate = new Date(timestamp);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    } catch (error) {
      return null;
    }
  }
  
  // 文字列の場合
  if (typeof exifDate === 'string') {
    try {
      let dateStr = exifDate.trim();
      
      // ISO 8601形式: "2024-01-01T12:00:00+09:00" など
      if (dateStr.includes('T') || dateStr.includes('+')) {
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }
      
      // 従来のEXIF形式: "2024:01:01 12:00:00"
      if (dateStr.includes(':')) {
        dateStr = dateStr.replace(/^(\d{4}):(\d{2}):(\d{2})(\s+\d{2}:\d{2}:\d{2})?/, '$1-$2-$3$4');
      }
      
      const parsedDate = new Date(dateStr);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    } catch (error) {
      return null;
    }
  }
  
  return null;
};

/**
 * Firestore Timestampを含むEXIFデータを安全に変換
 * @param exifData - EXIFデータオブジェクト
 * @returns 変換されたEXIFデータ
 */
export const convertFirestoreExifData = (exifData: any): any => {
  if (!exifData) return null;
  
  const converted = { ...exifData };
  
  // createDateの変換処理
  if (converted.createDate) {
    // Firebase Timestampの場合
    if (typeof converted.createDate === 'object' && converted.createDate.toDate) {
      converted.createDate = converted.createDate.toDate();
    }
    // 既にDateオブジェクトの場合
    else if (converted.createDate instanceof Date) {
      // 有効性チェック
      if (isNaN(converted.createDate.getTime())) {
        delete converted.createDate;
      }
    }
    // その他の形式の場合
    else {
      const parsedDate = parseExifDate(converted.createDate);
      if (parsedDate) {
        converted.createDate = parsedDate;
      } else {
        delete converted.createDate;
      }
    }
  }
  
  return converted;
};

/**
 * カードデータをFirestore保存用にクリーンアップ
 */
export const cleanCardDataForFirestore = (cardData: any): any => {
  const cleaned = cleanUndefinedValues(cardData);
  
  // createdAt と updatedAt を確実に保持
  if (cardData.createdAt) {
    if (cardData.createdAt instanceof Date && !isNaN(cardData.createdAt.getTime())) {
      cleaned.createdAt = cardData.createdAt;
    } else {
      // その他の形式の場合は再変換
      const validDate = parseExifDate(cardData.createdAt);
      if (validDate) {
        cleaned.createdAt = validDate;
      }
    }
  }
  
  if (cardData.updatedAt) {
    if (cardData.updatedAt instanceof Date && !isNaN(cardData.updatedAt.getTime())) {
      cleaned.updatedAt = cardData.updatedAt;
    } else {
      // その他の形式の場合は再変換
      const validDate = parseExifDate(cardData.updatedAt);
      if (validDate) {
        cleaned.updatedAt = validDate;
      }
    }
  }
  
  // 特にEXIFデータを注意深くチェック
  if (cleaned.exifData) {
    // EXIF各フィールドをチェック・修正
    Object.keys(cleaned.exifData).forEach(key => {
      const value = cleaned.exifData[key];
      
      if (value === undefined || value === null) {
        delete cleaned.exifData[key];
      } else if (key === 'createDate') {
        // createDateが既にDateオブジェクトの場合はそのまま保持
        if (value instanceof Date && !isNaN(value.getTime())) {
          // 有効なDateオブジェクトはそのまま保持
          cleaned.exifData[key] = value;
        } else {
          // その他の場合は再変換を試行
          const validDate = parseExifDate(value);
          if (validDate) {
            cleaned.exifData[key] = validDate;
          } else {
            delete cleaned.exifData[key];
          }
        }
      }
    });
    
    // EXIFデータが空になった場合は削除
    if (Object.keys(cleaned.exifData).length === 0) {
      delete cleaned.exifData;
    }
  }
  
  return cleaned;
};
